import React from "react";
import { connect } from "react-redux";
import { injectStripe } from "react-stripe-elements";
import { getQuote, getCurrentPlan, placeOrder, clearStore } from "./actions";
import { isValid } from "../../../helpers/functions";
import { bindActionCreators } from "redux";
import CartSection from "./CartSection";
import BillingInformation from "./BillingInformation";
import PaymentInformation from "./PaymentInformation";
import InvalidCoupon from "./../../InvalidCoupon";
import debounce from "lodash.debounce";
import "./MainForm.css";
import Error from "../../Error";
import messages from "../../../lang/default-messages";
import Spinner from "../../Spinner";
import { ECOMMERCE_CHECKOUT_VIEWED } from "../../../telemetry/constants";
import { log } from "../../../telemetry/analytics";
import { onCheckout } from "../../../gtm/ecommerce";
import { handleAnalyticsConsent } from "../../../osano";
import { Button, Anchor, Type } from "discover.medical.react-component-library";

const requiredFields = [
  "billing-first-name",
  "billing-last-name",
  "billing-address-1",
  "billing-country",
  "billing-city",
  "billing-state",
  "billing-zip",
];
let hideTaxSpinnerTimerId;
export class MainForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      body: null,
      billingFields: {
        "billing-first-name": {
          value: "",
          isValid: true,
        },
        "billing-last-name": {
          value: "",
          isValid: true,
        },
        "billing-address-1": {
          value: "",
          isValid: true,
        },
        "billing-address-2": {
          value: "",
          isValid: true,
        },
        "billing-country": {
          value: "US",
          label: "United States",
          isValid: true,
        },
        "billing-city": {
          value: "",
          isValid: true,
        },
        "billing-state": {
          value: "",
          isValid: true,
        },
        "billing-zip": {
          value: "",
          isValid: true,
        },
      },
      isBillingFilled: false,
      isCardFilled: false,
      showCardError: false,
      showSpinner: true,
      showTaxSpinner: false,
      errorMessage: "",
    };
    this.getQuoteDebounce = debounce(this.getQuoteDebounce, 1000);
  }

  componentDidMount() {
    this.props.getCurrentPlan(() => {
      this.setState({ showSpinner: false });
      if (this.props.currentPlan) {
        handleAnalyticsConsent(() => {
          log(ECOMMERCE_CHECKOUT_VIEWED, {
            id: this.props.currentPlan.id,
            price: this.props.currentPlan.price,
            name: this.props.currentPlan.description,
          });
          onCheckout(
            this.props.currentPlan.id,
            this.props.currentPlan.price,
            this.props.currentPlan.description,
          );
        });
      }
    });
  }

  componentWillUnmount() {
    if (this.props.error.status || this.props.tax || this.props.total) {
      this.props.clearStore();
    }
  }

  isPlaceOrderReady = () => {
    return (
      this.state.isBillingFilled && this.state.isCardFilled && this.props.total
    );
  };

  handleCardChange = ({ complete }) => {
    if (this.state.isCardFilled !== complete) {
      this.setState({ isCardFilled: complete });
    }
    if (complete === true) {
      this.setState({ showCardError: false });
    }
  };

  validateZipCode = (zipCode) => {
    return isValid("billing-zip", zipCode);
  };

  getQuoteDebounce = (data) => {
    hideTaxSpinnerTimerId = setTimeout(() => {
      this.setState({ showTaxSpinner: false });
    }, 9000);
    this.props.getQuote(
      this.props.currentPlan.id,
      data,
      () => {
        clearTimeout(hideTaxSpinnerTimerId);
        this.setState({ showTaxSpinner: false, errorMessage: "" });
      },
      this.handleError,
    );
  };

  handleBillingChange = (data, shouldSendRequest) => {
    this.setState({ billingFields: data });
    const areBillingFieldsFilled =
      requiredFields.every((el) => data[el].value) &&
      this.validateZipCode(data["billing-zip"].value);
    if (areBillingFieldsFilled) {
      this.setState({ isBillingFilled: true });
    } else {
      this.setState({ isBillingFilled: false });
    }

    if (
      this.props.currentPlan &&
      shouldSendRequest &&
      this.validateZipCode(data["billing-zip"].value)
    ) {
      this.setState({ showTaxSpinner: true });
      this.getQuoteDebounce(data);
    }

    this.setState({ body: data });
  };

  handleError = (errorMessage) => {
    if (errorMessage && typeof errorMessage === "string") {
      this.setState({ showSpinner: false, errorMessage });
    } else {
      this.setState({ showSpinner: false, errorMessage: "" });
    }
  };

  validateBillingField = () => {
    const { billingFields } = this.state;
    const billingFieldsKeys = Object.keys(billingFields);
    const validatedFields = {};
    let shouldUpdate = false;
    billingFieldsKeys.forEach((key) => {
      if (
        billingFields[key].value.length === 0 &&
        key !== "billing-address-2"
      ) {
        validatedFields[key] = { value: "", isValid: false };
        if (!shouldUpdate) {
          shouldUpdate = true;
        }
      } else {
        validatedFields[key] = billingFields[key];
      }
    });
    if (shouldUpdate) {
      this.setState({ billingFields: validatedFields, isBillingFilled: false });
    } else {
      this.setState({ isBillingFilled: true });
    }
  };

  handleSubmit = async (ev, stripe) => {
    ev.preventDefault();
    await this.validateBillingField();
    if (!this.state.isCardFilled) {
      await this.setState({ showCardError: true });
    }
    if (this.isPlaceOrderReady()) {
      this.setState({ showSpinner: true });
      this.props.placeOrder(
        this.state.body,
        stripe,
        this.props.goToThankYou,
        this.handleError,
      );
    }
  };

  createMarkup = (message) => {
    return { __html: message };
  };

  render() {
    const { stripe, tax, total, currentPlan, error } = this.props;
    const { showTaxSpinner, errorMessage, showCardError } = this.state;
    switch (error.status) {
      case 400:
        return <InvalidCoupon />;
      case 404:
        return (
          <Error
            title={messages.apiDown.title}
            message={messages.apiDown.message}
          />
        );
      default:
        return (
          <form
            className="checkout-form position-relative"
            onSubmit={(e) => this.handleSubmit(e, stripe)}
          >
            <Type
              element="h1"
              styledAs="h2"
              fontFamily="secondary"
              marginBottom="sm"
            >
              Checkout
            </Type>
            <div className="row">
              <div className="col-md-6">
                <BillingInformation
                  onFieldChange={this.handleBillingChange}
                  billingFields={this.state.billingFields}
                />
                <PaymentInformation
                  onChange={this.handleCardChange}
                  showCardError={showCardError}
                />
              </div>
              <div className="col-md-6 col-xl-4">
                <CartSection
                  showTaxSpinner={showTaxSpinner}
                  plan={currentPlan}
                  taxes={tax}
                  total={total}
                  error={!!errorMessage}
                />
                <div className="form-group">
                  <Button id="submit">Place order</Button>
                </div>
                {errorMessage ? (
                  <div
                    className="text-center text-danger"
                    data-auto="supportArea"
                  >
                    <div
                      dangerouslySetInnerHTML={this.createMarkup(errorMessage)}
                    />
                  </div>
                ) : (
                  <Type element="p" marginTop="md" fontSize="sm">
                    Need help with your purchase? Contact us at{" "}
                    <Anchor
                      href="mailto:medicalsupport@ebsco.com"
                      fontSize="sm"
                    >
                      medicalsupport@ebsco.com
                    </Anchor>
                  </Type>
                )}
              </div>
            </div>
            {this.state.showSpinner && (
              <Spinner className="position-absolute w-100 h-100" />
            )}
          </form>
        );
    }
  }
}
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getQuote,
      getCurrentPlan,
      placeOrder,
      clearStore,
    },
    dispatch,
  );

export const mapStateToProps = ({ quote }) => ({
  tax: quote.tax,
  total: quote.total,
  currentPlan: quote.currentPlan,
  error: quote.error,
});

export default injectStripe(
  connect(mapStateToProps, mapDispatchToProps)(MainForm),
);
